import type { View } from "../../../../common/types.ts";

type DraftHistoryPlayer = View<"draftHistory">["players"][number];
type DraftHistoryStat = View<"draftHistory">["summaryStat"];

export const getDraftHistoryDraftSlot = (player: DraftHistoryPlayer) =>
  player.draft.round >= 1 ? (player.draft.round - 1) * 30 + player.draft.pick : 999;

const statValue = (player: DraftHistoryPlayer, summaryStat: DraftHistoryStat) =>
  player.careerStats[summaryStat] ?? 0;

export const getDraftHistoryBestPick = (
  players: DraftHistoryPlayer[],
  summaryStat: DraftHistoryStat,
) =>
  [...players]
    .filter((player) => statValue(player, summaryStat) > 0)
    .sort((a, b) => statValue(b, summaryStat) - statValue(a, summaryStat))[0];

export const getDraftHistoryBiggestSteal = (
  players: DraftHistoryPlayer[],
  summaryStat: DraftHistoryStat,
) =>
  [...players]
    .filter((player) => statValue(player, summaryStat) > 0)
    .sort(
      (a, b) =>
        statValue(b, summaryStat) * getDraftHistoryDraftSlot(b) -
        statValue(a, summaryStat) * getDraftHistoryDraftSlot(a),
    )[0];

export const getDraftHistoryUnderperformer = (
  players: DraftHistoryPlayer[],
  summaryStat: DraftHistoryStat,
) => {
  if (!players.some((player) => statValue(player, summaryStat) > 0)) {
    return undefined;
  }

  return [...players]
    .filter((player) => player.draft.round === 1 && player.draft.pick <= 5)
    .sort((a, b) => statValue(a, summaryStat) - statValue(b, summaryStat))[0];
};

export const getDraftHistoryClassSummary = (
  players: DraftHistoryPlayer[],
  summaryStat: DraftHistoryStat,
) => ({
  awardWinners: players.filter((player) =>
    Object.values(player.awardCounts).some((count) => count > 0),
  ).length,
  hasCareerStats: players.some((player) => statValue(player, summaryStat) > 0),
  hallOfFamers: players.filter((player) => player.hof).length,
  players: players.length,
  undrafted: players.filter((player) => player.draft.round === 0).length,
});

export const getDraftHistoryUserTeamSummary = (
  players: DraftHistoryPlayer[],
  userTid: number,
  summaryStat: DraftHistoryStat,
) => {
  const picks = players.filter((player) => player.draft.tid === userTid);

  return {
    bestPick: getDraftHistoryBestPick(picks, summaryStat),
    hasCareerStats: picks.some((player) => statValue(player, summaryStat) > 0),
    picks: picks.length,
  };
};
